<template>
  <div>
    <FusionBaseDialog
      :open="showDialog"
      @close="close"
      persistentOverlay
      :title="$t('apiServices.registerDevices')"
      icon="mdi-map-marker-plus"
      padded
    >
      <p class="mt-0">{{ $t('apiServices.registrationFormat') }}</p>
      <p>{{ $t('apiServices.registrationSubscription') }}</p>
      <v-textarea
        v-model="bulkDevices"
        :placeholder="$t('apiServices.registerPlaceholder')"
        :rules="[bulkDevicesValid]"
        variant="outlined"
        data-cy="apiBulkDevices"
        @keypress="preventNonNumbers($event)"
      ></v-textarea>

      <template v-slot:actions>
        <v-btn
          @click="close"
          data-cy="closeRegisterDevicesDialog"
          variant="outlined"
          class="text-black cancel-btn"
          :disabled="bulkSpinner"
        >
          {{ $t('buttons.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="text"
          class="bg-primary text-white"
          :loading="bulkSpinner"
          :disabled="submitDisabled"
          @click="bulkRegisterExternalDevices"
          data-cy="apiBulkRegisterSubmit"
        >
          {{ $t('buttons.submit') }}
        </v-btn>
      </template>
    </FusionBaseDialog>
    <AccessAdditionalFeaturesDialog
      :open="dialogs.accessAdditionalFeatures.open"
      :addToBoth="addToBothWebsiteAndDataServices"
      :subscriptionTiers="subscriptionTiers"
      :bulkDeviceModels="bulkDeviceModels"
      @change="changeAddToBothWebsiteAndDataServices"
      @subTierChange="changeSubscriptionTiers"
      @cancel="cancelAccessAdditionalFeaturesDialog"
      @close="closeAccessAdditionalFeaturesDialog"
    />

    <APIBulkRegistrationInfoDialog
      :open="dialogs.apiBulkRegistrationInfo.open"
      :isRetrying="dialogs.apiBulkRegistrationInfo.isRetrying"
      :addToBoth="addToBothWebsiteAndDataServices"
      :allowRetry="allowRetry"
      :results="results"
      @retry="retryBulkRegistration"
      @close="closeAPIBulkRegistrationInfoDialog"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { subscriptionTypes, deviceModels } from '@/store/subscriptions/utils'
import { NAMESPACES } from '@/store/types'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'
import { HTTPStatus } from '@/api'
import { serialIsValid } from '@/services/device-serial'
import { DEVICE_DEFAULT_CONFIG } from '@/services/device-config'
import type { RegisterDeviceRequest, DeviceRegistered } from '@/types/devices'
import type { ValidDevice, RegistrationResult, RegistrationSubType } from '@/types/api'
import AccessAdditionalFeaturesDialog from './AccessAdditionalFeaturesDialog.vue'
import APIBulkRegistrationInfoDialog from './APIBulkRegistrationInfoDialog.vue'

const NO_ATTEMPT_MSG = 'NoAttempt'

interface LogResultOpts {
  serial: string
  status: number | null
  msg: string
  subType: RegistrationSubType
}

interface ResultValues {
  success: boolean | null
  message: string
}

export default defineComponent({
  name: 'RegisterDevicesDialog',
  props: {
    open: Boolean,
    removeLock: Boolean,
    subLock: Boolean
  },
  emits: ['close', 'getUserDevices'],
  data() {
    return {
      bulkDevices: '',
      validDevices: [] as ValidDevice[],
      results: [] as RegistrationResult[],
      bulkSpinner: false,
      addToBothWebsiteAndDataServices: false,
      subscriptionTiers: {
        [deviceModels.BLUESKY]: null,
        [deviceModels.BLUESKY_GAS]: null,
        [deviceModels.AIRASSURE]: null
      } as Record<string, string | null>,
      dialogs: {
        accessAdditionalFeatures: {
          open: false
        },
        apiBulkRegistrationInfo: {
          open: false,
          isRetrying: false
        }
      }
    }
  },
  components: {
    AccessAdditionalFeaturesDialog,
    APIBulkRegistrationInfoDialog
  },
  computed: {
    bulkDevicesValidFlag(): boolean {
      if (this.bulkDevices.length === 0) {
        return true
      }

      const bulkDeviceSerials = this.bulkDevices.split(', ').map((x) => x.trim())

      // limit to 100 devices registered at once
      if (bulkDeviceSerials.length > 101) {
        return false
      }

      for (const serial of bulkDeviceSerials) {
        const deviceModelNumber = serial.substring(0, 4)

        const serialLength =
          this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SERIAL_LENGTH}`
          ](deviceModelNumber)

        if (!serialIsValid(deviceModelNumber, serial, serialLength)) {
          return false
        }
      }

      return true
    },
    showDialog(): boolean {
      return (
        this.open &&
        !this.dialogs.accessAdditionalFeatures.open &&
        !this.dialogs.apiBulkRegistrationInfo.open
      )
    },
    bulkDeviceModels(): string[] {
      const commaSeparatorRegex = /[\s,]+/
      const serials = this.bulkDevicesValidFlag ? this.bulkDevices.split(commaSeparatorRegex) : []
      return Array.from(new Set(serials.map((serial) => serial.slice(0, 4))))
    },
    bulkDevicesValid(): boolean | string {
      return this.bulkDevicesValidFlag || this.$t('apiServices.invalidList')
    },
    submitDisabled(): boolean {
      return (
        this.bulkSpinner ||
        !this.bulkDevicesValidFlag ||
        this.bulkDevices.length === 0 ||
        this.removeLock ||
        this.subLock
      )
    },
    retryValidDevices(): ValidDevice[] {
      const devsList = this.results.filter((result) => {
        return (
          result[subscriptionTypes.DATA_SERVICES].allowRetry ||
          result[subscriptionTypes.WEBSITE].allowRetry
        )
      })
      const devices: ValidDevice[] = []
      for (const dev of devsList) {
        const serial = dev.serial
        const model = serial.slice(0, 4)
        devices.push({
          model,
          serial
        })
      }
      return devices
    },
    allowRetry(): boolean {
      return this.retryValidDevices.length > 0
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    preventNonNumbers(event: KeyboardEvent) {
      const allowedKeys = [' ', ',', 'Backspace']
      if (!allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
        event.preventDefault()
      }
    },
    changeSubscriptionTiers(value: Record<string, string | null>) {
      this.subscriptionTiers = value
    },
    cancelAccessAdditionalFeaturesDialog() {
      this.validDevices = []
      this.dialogs.accessAdditionalFeatures.open = false
    },
    changeAddToBothWebsiteAndDataServices(value: boolean) {
      this.addToBothWebsiteAndDataServices = value
    },
    async closeAccessAdditionalFeaturesDialog() {
      await this.processBulkRegistration()
      this.dialogs.accessAdditionalFeatures.open = false
    },
    closeAPIBulkRegistrationInfoDialog() {
      this.dialogs.apiBulkRegistrationInfo.open = false
      this.results = []
      this.close()
    },
    async retryBulkRegistration() {
      this.dialogs.apiBulkRegistrationInfo.isRetrying = true
      await this.processBulkRegistration(this.retryValidDevices)
      this.dialogs.apiBulkRegistrationInfo.isRetrying = false
    },
    async patchSubscription(device: ValidDevice, isExternal: boolean) {
      const resp = await this.$api.patchDeviceSubscription({
        isExternal,
        ...device
      })
      const body = await resp.text()
      this.logResult({
        serial: device.serial,
        status: resp.status,
        msg: body,
        subType: isExternal ? subscriptionTypes.DATA_SERVICES : subscriptionTypes.WEBSITE
      })
    },
    async registerDevice(device: ValidDevice): Promise<number> {
      const resp = await this.$api.postRegisterDevice(device as unknown as RegisterDeviceRequest)
      let body: DeviceRegistered | string = ''
      if (resp.status === HTTPStatus.Created) {
        body = await resp.json()
        this.logResult({
          serial: device.serial,
          status: resp.status,
          msg: (body as DeviceRegistered).cloud_device_id,
          subType: subscriptionTypes.DATA_SERVICES
        })
        await this.$api.postDeviceConfig(
          DEVICE_DEFAULT_CONFIG,
          (body as DeviceRegistered).cloud_device_id
        )
      } else {
        body = await resp.text()
        this.logResult({
          serial: device.serial,
          status: resp.status,
          msg: body,
          subType: subscriptionTypes.DATA_SERVICES
        })
      }
      return resp.status
    },
    async processBulkRegistration(devices?: ValidDevice[]) {
      const deviceList = devices ?? this.validDevices
      this.bulkDevices = ''
      this.bulkSpinner = true

      for (const d of deviceList) {
        const device = {
          model: d.model,
          serial: d.serial,
          subscription: subscriptionTypes.DATA_SERVICES
        }
        await this.processRegistration(device)
      }
      this.bulkSpinner = false
      this.dialogs.apiBulkRegistrationInfo.open = true
      this.$emit('getUserDevices')
    },
    async processRegistration(device: ValidDevice) {
      // Device is already registered and associated with a website subscription,
      // patch data services onto it
      if (this.deviceInWebsite(device.serial)) {
        await this.patchSubscription(device, true)
        if (this.addToBothWebsiteAndDataServices) {
          const msg = 'Device already registered to your account'
          this.logResult({
            serial: device.serial,
            status: null,
            msg: msg,
            subType: subscriptionTypes.WEBSITE
          })
        }
      } else {
        // register device with data service subscription first
        const status = await this.registerDevice(device)
        if (this.addToBothWebsiteAndDataServices) {
          if (status === HTTPStatus.Created) {
            // add website subscription
            device.subscription = this.getSubscriptionByModel(device.model)
            await this.patchSubscription(device, false)
          } else {
            const msg = NO_ATTEMPT_MSG
            this.logResult({
              serial: device.serial,
              status: null,
              msg: msg,
              subType: subscriptionTypes.WEBSITE
            })
          }
        }
      }
    },
    getSubscriptionByModel(model: string): string | null {
      return this.subscriptionTiers[model] || null
    },
    deviceOnSub(serial: string, subType: RegistrationSubType): boolean {
      let deviceOnSub = false
      if (subType === subscriptionTypes.WEBSITE) {
        if (
          this.deviceInWebsite(serial) ||
          this.deviceInBasic(serial) ||
          this.deviceInPremium(serial)
        ) {
          deviceOnSub = true
        }
      }
      if (subType === subscriptionTypes.DATA_SERVICES) {
        if (this.deviceInDataServices(serial)) {
          deviceOnSub = true
        }
      }
      return deviceOnSub
    },
    hasSubscription(model: string, subType: RegistrationSubType): boolean {
      let hasSub = false
      if (subType === subscriptionTypes.WEBSITE) {
        const basicSubIDs = this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_BASIC}`
        ].map((sub: { id: string }) => sub.id)
        const premiumSubIDs = this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_PREMIUM}`
        ].map((sub: { id: string }) => sub.id)
        if (
          basicSubIDs.some((sub: string) => sub?.includes(model)) ||
          premiumSubIDs.some((sub: string) => sub?.includes(model))
        ) {
          hasSub = true
        }
      }
      if (subType === subscriptionTypes.DATA_SERVICES) {
        const subIDs = this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DATA_SERVICES}`
        ].map((sub: { id: string }) => sub.id)
        if (subIDs.some((sub: string) => sub?.includes(model))) {
          hasSub = true
        }
      }
      return hasSub
    },
    setResultValues(opts: LogResultOpts): ResultValues {
      const { serial, status, msg, subType } = opts
      let success: boolean | null = null
      let message = msg

      if (status === HTTPStatus.OK || status === HTTPStatus.Created) {
        success = true
        message = this.$t('bulkRegistrationInfoDialog.messages.registrationSuccessful')
      } else if (
        status === HTTPStatus.InternalServerError ||
        status === HTTPStatus.NotAcceptable ||
        status === HTTPStatus.UnprocessableEntity ||
        msg.includes('error activating subscription')
      ) {
        success = false
        message = this.$t('bulkRegistrationInfoDialog.messages.registrationFailed')
      } else if (status === HTTPStatus.NotFound) {
        success = false
        message = this.$t('bulkRegistrationInfoDialog.messages.serialNumberIncorrect')
      } else if (
        msg.includes('Device already registered to your account') ||
        (msg.includes('EntityDuplicateError') && subType === subscriptionTypes.DATA_SERVICES)
      ) {
        success = true
        message = this.$t('bulkRegistrationInfoDialog.messages.alreadyRegistered')
      } else if (msg.includes('Device is already registered to another account')) {
        success = false
        message = this.$t('bulkRegistrationInfoDialog.messages.existsAnotherAccount')
      } else if (msg.includes('maximum number of registered devices reached')) {
        // response message is the same for max devices reached whether user has a subscription or not
        // check whether device subscription exists and change message accordingly
        success = false
        const model = serial.slice(0, 4)
        const hasSub = this.hasSubscription(model, subType)

        message = hasSub
          ? this.$t('bulkRegistrationInfoDialog.messages.maxDevices')
          : this.$t('bulkRegistrationInfoDialog.messages.noSubscription')
      } else if (msg.includes(NO_ATTEMPT_MSG)) {
        message = this.$t('bulkRegistrationInfoDialog.messages.noAttempt')
      }

      return {
        success,
        message
      }
    },
    logResult(opts: LogResultOpts) {
      const { serial, status, subType } = opts
      // check whether device already exists in results
      const found = this.results.find((result) => result.serial === serial)
      // device not found, add with default state
      if (!found) {
        this.results.push({
          serial: serial,
          [subscriptionTypes.DATA_SERVICES]: {
            success: null,
            allowRetry: false,
            status: null,
            msg: ''
          },
          [subscriptionTypes.WEBSITE]: {
            success: null,
            allowRetry: false,
            status: null,
            msg: ''
          }
        })
      }
      // get index of device in results
      const index = this.results.findIndex((result) => result.serial === serial)
      const values = this.setResultValues(opts)
      const subResult = this.results[index][subType]
      subResult.success = values.success
      subResult.msg = values.message
      subResult.status = status
      // only status 500 should allow retry
      subResult.allowRetry = status === HTTPStatus.InternalServerError
    },
    deviceInDataServices(serial: string): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_DATA_SERVICES_SUBSCRIPTION}`
      ](serial)
    },
    deviceInWebsite(serial: string): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_WEBSITE_SUBSCRIPTION}`
      ](serial)
    },
    deviceInBasic(serial: string): boolean {
      //if there are devices in basic
      if (
        this.$store.getters[`${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DEVICE_BASIC}`]
      ) {
        //then check if this device is in there
        if (
          this.$store.getters[
            `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DEVICE_BASIC_SUBSCRIPTION_BY_DEVICE_SERIAL}`
          ](serial)
        ) {
          return true
        }
      }
      return false
    },
    deviceInPremium(serial: string): boolean {
      //if there are devices in premium
      if (
        this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DEVICE_PREMIUM}`
        ]
      ) {
        //then check if this device is in there
        if (
          this.$store.getters[
            `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DEVICE_PREMIUM_SUBSCRIPTION_BY_DEVICE_SERIAL}`
          ](serial)
        ) {
          return true
        }
      }
      return false
    },
    async bulkRegisterExternalDevices() {
      const devsListString = this.bulkDevices.replace(/\s/g, '')

      const devsList = devsListString.split(',')
      const models: string[] = []
      this.validDevices = []
      let unsubscribedDeviceExists = false

      for (const dev of devsList) {
        const model = dev.slice(0, 4)
        const serialLength =
          this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SERIAL_LENGTH}`
          ](model)
        if (serialIsValid(model, dev, serialLength)) {
          if (!models?.includes(model)) {
            models.push(model)
          }
          unsubscribedDeviceExists ||= !this.deviceInWebsite(dev)
          this.validDevices.push({
            model: model,
            serial: dev
          })
        }
      }

      if (this.validDevices.length) {
        //Before Data Service registration,
        //check if has available Website subscriptions
        //and if so pop up the AccessAdditionalFeaturesDialog

        const subscriptionsAvailable =
          models
            .map((model) =>
              this.$store.getters[
                `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_BASIC_BY_MODEL}`
              ](model)
            )
            .includes(true) ||
          models
            .map((model) =>
              this.$store.getters[
                `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL}`
              ](model)
            )
            .includes(true)
        if (subscriptionsAvailable && unsubscribedDeviceExists) {
          this.dialogs.accessAdditionalFeatures.open = true
        } else {
          //otherwise just process
          this.addToBothWebsiteAndDataServices = false
          return this.processBulkRegistration()
        }
      }
    }
  }
})
</script>
<style lang="scss" scoped>
.cancel-btn {
  margin-right: 12px;
}
</style>
